import type { Schemas } from '@/lib/api/client'

export type PipelineStatus = Schemas['PipelineStatus']
export type EvaluationTab = Schemas['EvaluationTab']
export type ServiceHistory = Schemas['ServiceHistory']
export type AcquisitionType = Schemas['AcquisitionType']
export type AustralianState = Schemas['AustralianState']

/** A status mark's variant: its dot and words in one ink (docs/design/design-system/components/Pill). */
export type PillTone = 'default' | 'good' | 'bad' | 'muted'

/** The words and the pill for each status, and for none ("Needs a decision"). */
export type StatusLabel = { label: string; tone: PillTone }

/**
 * The status marks as the Forecourt boards draw them (FcEvals, FcEvaluation): Needs a decision in
 * the red, as what needs a person; Follow up in the ink, a workflow state rather than a judgement;
 * Traded good; Lost muted. Purchased is an acquisition like Traded; Pending waits like Follow up.
 */
export const STATUS: Record<PipelineStatus, StatusLabel> = {
  traded: { label: 'Traded', tone: 'good' },
  purchased: { label: 'Purchased', tone: 'good' },
  pending: { label: 'Pending', tone: 'default' },
  follow_up: { label: 'Follow up', tone: 'default' },
  lost_trade_sold_elsewhere: { label: 'Lost: trade sold elsewhere', tone: 'muted' },
  lost_customer_keeping_trade: { label: 'Lost: customer keeping trade', tone: 'muted' },
  lost_purchasing_elsewhere: { label: 'Lost: buying elsewhere', tone: 'muted' },
  lost_not_buying: { label: 'Lost: not buying', tone: 'muted' },
}

export const NEEDS_DECISION: StatusLabel = { label: 'Needs a decision', tone: 'bad' }

export function statusLabel(status: PipelineStatus | null): StatusLabel {
  return status === null ? NEEDS_DECISION : STATUS[status]
}

/** The status menu's choices, in order: the open ones, then the four Lost reasons as a group. */
export const OPEN_STATUSES: PipelineStatus[] = ['traded', 'purchased', 'pending', 'follow_up']

export const LOST_STATUSES: PipelineStatus[] = [
  'lost_trade_sold_elsewhere',
  'lost_customer_keeping_trade',
  'lost_purchasing_elsewhere',
  'lost_not_buying',
]

/** What each Lost reason reads as inside the menu's Lost group. */
export const LOST_REASON: Record<string, string> = {
  lost_trade_sold_elsewhere: 'Trade sold elsewhere',
  lost_customer_keeping_trade: 'Customer keeping trade',
  lost_purchasing_elsewhere: 'Buying elsewhere',
  lost_not_buying: 'Not buying',
}

export const ACQUIRED: PipelineStatus[] = ['traded', 'purchased']

/** The list's tabs, in the designed order. */
export const TABS: { key: EvaluationTab; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'needs_decision', label: 'Needs a decision' },
  { key: 'follow_up', label: 'Follow up' },
  { key: 'traded', label: 'Traded' },
  { key: 'lost', label: 'Lost' },
  // The signed-in appraiser's own drafts: no other tab lists a draft.
  { key: 'drafts', label: 'Drafts' },
]

export const SERVICE_HISTORY: Record<ServiceHistory, string> = {
  full_genuine: 'Full genuine',
  full_non_genuine: 'Full non-genuine',
  full_mixed: 'Full mixed',
  partial: 'Partial',
  missing: 'Missing',
}

export const ACQUISITION_TYPE: Record<AcquisitionType, string> = {
  trade_in: 'Trade-in',
  private_purchase: 'Private purchase',
  fleet: 'Fleet',
}

export const STATES: AustralianState[] = ['VIC', 'NSW', 'QLD', 'SA', 'WA', 'TAS', 'ACT', 'NT']

/** The reconditioning amounts, in the order the step and the eval page list them. */
export const RECONDITIONING = [
  { key: 'recon_detail_cents', label: 'Detail' },
  { key: 'recon_rims_cents', label: 'Rims' },
  { key: 'recon_paint_panel_cents', label: 'Paint and panel' },
  { key: 'recon_interior_cents', label: 'Interior' },
  { key: 'recon_windscreen_cents', label: 'Windscreen' },
  { key: 'recon_rwc_cents', label: 'RWC' },
  { key: 'recon_rego_cents', label: 'Rego' },
] as const

export type ReconKey = (typeof RECONDITIONING)[number]['key']

export const AFTERMARKET = [
  { key: 'window_tint', label: 'Window tint' },
  { key: 'paint_protection', label: 'Paint protection' },
  { key: 'interior_protection', label: 'Interior protection' },
  { key: 'rust_proofing', label: 'Rust proofing' },
] as const

/** The yes or no answers the Condition step draws as switches, after the aftermarket. */
export const CHECKS = [
  { key: 'vehicle_sighted', label: 'Vehicle sighted' },
  { key: 'spare_key', label: 'Spare key' },
  { key: 'vehicle_driven', label: 'Vehicle driven' },
  { key: 'written_off', label: 'Written off' },
  { key: 'financed', label: 'Financed' },
] as const

export type SwitchKey = (typeof AFTERMARKET)[number]['key'] | (typeof CHECKS)[number]['key']

/** A vehicle's one-line name: `2019 BMW 330i`. */
export function vehicleName(vehicle: { year: number; make: string; model: string }): string {
  return `${vehicle.year} ${vehicle.make} ${vehicle.model}`
}
